// One runtime per document, including React remounts during public navigation.
const SCRIPT_ID = "orkelys-ga4";
const DENIED = { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" };
const configured = new Set();
let initialized = false;
let lastVisit = "";
let activeId = "";

export const analyticsCookiePrefix = id => `orkelys_${String(id || "").replace(/[^A-Z0-9]/gi, "")}`;

function clearOwnedCookies(id) {
  if (!/^G-[A-Z0-9]{6,20}$/.test(id || "")) return;
  const base = `${analyticsCookiePrefix(id)}_ga`;
  const domains = ["", window.location.hostname, `.${window.location.hostname}`];
  document.cookie.split(";").forEach(cookie => {
    const name = cookie.split("=")[0].trim();
    if (name !== base && !name.startsWith(`${base}_`)) return;
    domains.forEach(domain => {
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax; Secure${domain ? `; domain=${domain}` : ""}`;
    });
  });
}

export function pauseAnalytics(id) {
  if (configured.has(id)) window[`ga-disable-${id}`] = true;
}

export function revokeAnalytics(id) {
  pauseAnalytics(id);
  if (initialized && activeId === id && window.gtag?.orkelysOwned) window.gtag("consent", "update", DENIED);
  clearOwnedCookies(id);
  // Removing a script cannot unload an executed library. The caller reloads on withdrawal.
  const script = document.getElementById(SCRIPT_ID);
  if (activeId === id && script?.dataset.orkelysAnalytics === "true") script.remove();
  return initialized && activeId === id;
}

export function resumeAnalytics(id, fields) {
  // Never take ownership of an independently installed tracker.
  if (window.gtag && !window.gtag.orkelysOwned) return false;
  if (window.dataLayer && !window.dataLayer.orkelysOwned) return false;
  const existingScript = document.getElementById(SCRIPT_ID);
  if (existingScript && existingScript.dataset.orkelysAnalytics !== "true") return false;
  if (activeId && activeId !== id) pauseAnalytics(activeId);
  if (!initialized) {
    window.dataLayer = [];
    window.dataLayer.orkelysOwned = true;
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag.orkelysOwned = true;
    window.gtag("consent", "default", DENIED);
    window.gtag("js", new Date());
    initialized = true;
  }
  activeId = id;
  window[`ga-disable-${id}`] = false;
  window.gtag("set", { ...fields, campaign_id: "", campaign_source: "", campaign_medium: "", campaign_name: "", campaign_term: "", campaign_content: "" });
  window.gtag("consent", "update", { ...DENIED, analytics_storage: "granted" });
  if (!configured.has(id)) {
    window.gtag("config", id, { ...fields, send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false, cookie_expires: 15552000, cookie_update: false, cookie_prefix: analyticsCookiePrefix(id), cookie_domain: window.location.hostname, cookie_path: "/", cookie_flags: "SameSite=Lax;Secure" });
    configured.add(id);
  }
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.dataset.orkelysAnalytics = "true";
    script.async = true;
    script.referrerPolicy = "no-referrer";
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    script.onerror = () => { pauseAnalytics(id); script.remove(); };
    document.head.appendChild(script);
  }
  return true;
}

export function recordPageView(id, fields, visitKey) {
  const key = `${id}:${visitKey}`;
  if (lastVisit === key || activeId !== id || !configured.has(id) || window[`ga-disable-${id}`] || !window.gtag?.orkelysOwned) return;
  lastVisit = key;
  window.gtag("event", "page_view", { ...fields, send_to: id });
}

export function recordJourneyClick(id, fields, target) {
  if (activeId !== id || !configured.has(id) || window[`ga-disable-${id}`] || !window.gtag?.orkelysOwned) return;
  const link = target?.closest?.("a[href]");
  if (!link) return;
  let url;
  try { url = new URL(link.href, window.location.origin); } catch { return; }
  if (url.origin !== window.location.origin) return;
  const name = { "/prendre-rendez-vous": "booking_open", "/contact": "contact_open", "/seances-tarifs": "pricing_open" }[url.pathname.replace(/\/$/, "")];
  if (name) window.gtag("event", name, { ...fields, send_to: id });
}
